import styles from "./FolderTreeItem.module.css";
import { Icon } from "@zapier/zinnia-icons";
import { Text } from "@zapier/design-system";
import classNames from "classnames";
import Link, { type LinkProps } from "next/link";
import { TooltipText } from "../../../../common/Table/TooltipText";

interface FolderItemProps {
  text: string;
  onClick?: () => void;
  href?: string;
  isSelected?: boolean;
  role?: "treeitem" | "none";
  "aria-expanded"?: boolean;
  "aria-current"?: "page";
  onKeyDown?: (event: React.KeyboardEvent) => void;
  "aria-owns"?: string;
  tabIndex?: number;
  onToggleExpanded?: (folderId?: string) => void;
  folderId?: string | null;
  isShared?: boolean;
  /** When true, renders as a plain collapsible section header:
   *  no folder icon, no active state, subdued text styling,
   *  and the caret does not intercept clicks. */
  isAccordionHeader?: boolean;
}

const FolderTreeItem = ({
  text,
  href,
  onClick,
  isSelected,
  role = "none",
  "aria-expanded": ariaExpanded,
  "aria-current": ariaCurrent,
  "aria-owns": ariaOwns,
  onKeyDown,
  tabIndex,
  onToggleExpanded,
  folderId,
  isShared,
  isAccordionHeader = false,
}: FolderItemProps) => {
  const Tag = href ? Link : "button";
  return (
    <Tag
      href={href as LinkProps["href"]}
      onClick={onClick}
      onKeyDown={onKeyDown}
      tabIndex={tabIndex}
      className={classNames(styles.folderItem, {
        [styles.folderItemSelected]: isSelected && !isAccordionHeader,
      })}
      // Anchor tags should keep their default "link" role for accessibility.
      // role={undefined} will keep the default role "link" on the element.
      // For permitted ARIA roles, see https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/a#technical_summary
      role={href && role === "none" ? undefined : role}
      aria-expanded={ariaOwns ? ariaExpanded : undefined}
      aria-current={ariaCurrent}
      aria-owns={ariaOwns}
    >
      {ariaOwns && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events -- caret is a secondary mouse-only toggle nested inside the row element; it cannot be a nested button and the row handles keyboard activation
        <span
          className={styles.caretContainer}
          onClick={
            isAccordionHeader
              ? undefined
              : (event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (folderId) {
                    onToggleExpanded?.(folderId);
                  }
                }
          }
        >
          <Icon
            name="arrowSmallDown"
            size={16}
            className={`${styles.expandIcon} ${ariaExpanded ? styles.expandIconOpen : styles.expandIconClosed}`}
            color="GrayWarm6"
            aria-hidden={true}
          />
        </span>
      )}
      {!isAccordionHeader && (
        <Icon
          name={isShared ? "team" : "navFolder"}
          color={isSelected ? "BrandOrange" : "GrayWarm6"}
          className={styles.folderItemIcon}
          size="20"
          aria-hidden={true}
        />
      )}
      <TooltipText
        label={text}
        id={`folder-tree-item-${folderId}`}
        placement="right"
        shouldNotBeFocusable={true}
      >
        <Text
          type={isAccordionHeader ? "Body1" : isSelected ? "Body3" : "Body1"}
          color={isAccordionHeader ? undefined : "TextDefault"}
          className={classNames(styles.folderItemText, {
            [styles.accordionHeaderText]: isAccordionHeader,
          })}
        >
          {text}
        </Text>
      </TooltipText>
    </Tag>
  );
};

export default FolderTreeItem;
